import Head from 'next/head';
import Script from 'next/script';
import { MetadataModel } from '@/types';
import { useContext } from 'react';
import { SystemFeaturesContext, UserContext } from '../ContextProviders';
import { parseCMSScripts, sanitizeScriptAttrs, ScriptTagModel } from '@/utils';

export interface IMetadata {
  metadata?: MetadataModel;
}

const Metadata = ({ metadata }: IMetadata) => {
  const { realHost } = useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  if (!metadata) return;

  let vanillaScripts: string[] | null = null;
  const scriptSrcs: ScriptTagModel[] = [];

  if (metadata.headSectionScripts && metadata.headSectionScripts.length > 0) {
    vanillaScripts = metadata.headSectionScripts[0]
      ?.split(/<\/script>/g)
      ?.map((s) => {
        if (!s || s === '') return null;
        return s.replace(/<script[^>]*>/g, '').replace(/<\/script>/g, '');
      })
      .filter((s) => s !== null);

    metadata.headSectionScripts[0]?.split('\n')?.forEach((s) => {
      if (s && s !== '') {
        const match = /src\s*=\s*"([^"]+)/g.exec(s);
        if (match) {
          match
            .filter((res) => !res.startsWith('src='))
            .forEach((src) => {
              scriptSrcs.push({ src, hid: src.replace(/[^a-zA-Z]/g, '') });
            });
        }
      }
    });
  }
  const hreflangs: React.JSX.Element[] = [];

  if (metadata.scripts) {
    metadata.scripts.forEach((script) => {
      if (script.src) {
        let extraAttributes = {};
        if (script.extraAttributes) {
          try {
            extraAttributes = sanitizeScriptAttrs(JSON.parse(script.extraAttributes || '{}'));
          } catch {
            console.error(`Invalid script URL: ${script.src}`);
          }
        }
        const finalScript = { src: script.src, hid: script.src.replace(/[^a-zA-Z]/g, ''), ...extraAttributes };

        scriptSrcs.push(finalScript);
      } else if (script.body) {
        const vScript = parseCMSScripts(script.body);
        if (vScript?.vanillaScripts?.innerHTML) {
          const finalScript = vScript.vanillaScripts.innerHTML
            .replaceAll('{USER_SALES_FORCE_ID}', user?.salesforceId || '')
            .replaceAll('{USER_EMAIL_ADDRESS}', user?.email || '');
          vanillaScripts = [...(vanillaScripts || []), finalScript || ''].filter((s) => s !== '');
        }
      }
    });
  }

  if (metadata.languages && metadata.languages.length > 0) {
    let currentHost = null;
    if (process.env.NODE_ENV === 'development') {
      currentHost =
        (process.env.NEXT_PUBLIC_DEV_FRONTEND_HOST !== '' && process.env.NEXT_PUBLIC_DEV_FRONTEND_HOST) ||
        'https://uat.testing-stylus.com/';
    } else {
      if (typeof window === 'undefined') {
        currentHost = realHost || 'https://uat.testing-stylus.com';
      } else {
        currentHost = window?.location?.origin;
      }
    }

    metadata.languages.forEach((language) => {
      if (language.language === 'en-GB') {
        hreflangs.push(<link rel="alternate" href={currentHost + language.languageUrl} hrefLang="x-default" />);
      }

      hreflangs.push(
        <link
          rel="alternate"
          href={currentHost + language.languageUrl}
          hrefLang={language.language === 'en-GB' ? 'en' : language.language}
        />,
      );
    });
  }

  return (
    <>
      {scriptSrcs &&
        scriptSrcs.map(({ hid, src, ...props }) => (
          // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
          <Script key={hid} src={src} {...props} strategy="beforeInteractive" />
        ))}

      {vanillaScripts &&
        vanillaScripts?.length > 0 &&
        vanillaScripts.map((s, index) => (
          // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
          <Script
            dangerouslySetInnerHTML={{ __html: s }}
            id={`vanilla-script-${index}`}
            key={s}
            strategy="beforeInteractive"
          />
        ))}

      <Head>
        <title>{metadata.pageTitle}</title>

        <meta name="description" content={metadata.metaDescription} />
        <meta property="og:url" content={metadata.absolutePageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metadata.openGraphPageTitle} />
        <meta property="og:description" content={metadata.openGraphDescription} />
        <link rel="canonical" href={metadata.absolutePageUrl} />

        {...hreflangs}

        {!metadata.isPublic && <meta name="robots" content="noindex" />}
      </Head>
    </>
  );
};

export default Metadata;
